import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@zg-rentals/react-query5';
import { EMPTY_PROPERTY } from '../constants';
import { TABS } from '../components/Layout';
import type {
  GetYourRentalPageDataWebApiResponseDto,
  YourRentalPropertyDto,
} from '@zg-rentals/ts-your-rental-api-client';
import { getRinsUpsell } from '../queries/getRinsUpsell';
import { client } from '../rentalClient';

interface UseYourRentalPageProps {
  rentalsData?: GetYourRentalPageDataWebApiResponseDto;
  encodedZuid: string;
}

interface UseYourRentalPageReturn {
  selectedProperty: YourRentalPropertyDto | undefined;
  allProperties: Array<YourRentalPropertyDto>;
  defaultProperty: YourRentalPropertyDto | undefined;
  showBreadcrumb: boolean;

  selectedTab: string;
  setSelectedTab: (tab: string) => void;

  handlePropertyChange: (property: YourRentalPropertyDto) => void;
  handleAddRental: () => void;
  handleBreadcrumbClick: () => void;
  isLoadingRinsUpsellData: boolean;
}

/**
 * Handles:
 * - Property selection and management
 * - Tab state (Rent, Documents)
 * - Property addition logic
 */
export const useYourRentalPage = ({ rentalsData, encodedZuid }: UseYourRentalPageProps): UseYourRentalPageReturn => {
  const [selectedProperty, setSelectedProperty] = useState<YourRentalPropertyDto | undefined>(undefined);
  const [addedProperties, setAddedProperties] = useState<Array<YourRentalPropertyDto>>([]);
  const [selectedTab, setSelectedTab] = useState<string>(TABS.rent);
  const [fetchRinsUpsellData, setFetchRinsUpsellData] = useState(false);

  const {
    data: rinsUpsellData,
    isLoading: isLoadingRinsUpsellData,
    isError: isErrorRinsUpsellData,
  } = useQuery({
    ...getRinsUpsell(encodedZuid, client),
    enabled: fetchRinsUpsellData,
  });

  const allProperties = useMemo(
    () => [...(rentalsData?.yourRentalPageData.yourRentalProperties ?? []), ...addedProperties],
    [rentalsData?.yourRentalPageData.yourRentalProperties, addedProperties],
  );

  const defaultProperty = useMemo(
    () =>
      allProperties.length > 0 ? allProperties[rentalsData?.yourRentalPageData.defaultPropertyIndex ?? 0] : undefined,
    [allProperties, rentalsData?.yourRentalPageData.defaultPropertyIndex],
  );

  const showBreadcrumb = useMemo(
    () => allProperties.length > 0 && selectedProperty !== defaultProperty,
    [allProperties.length, selectedProperty, defaultProperty],
  );

  useEffect(() => {
    if (defaultProperty) {
      setSelectedProperty(defaultProperty);
    }
  }, [defaultProperty]);

  const handlePropertyChange = useCallback((property: YourRentalPropertyDto): void => {
    setSelectedProperty(property);
    setSelectedTab(TABS.rent);
  }, []);

  const handleAddRental = useCallback((): void => {
    if (!encodedZuid || encodedZuid.trim() === '') {
      setAddedProperties((prev) => [...prev, EMPTY_PROPERTY]);
      setSelectedProperty(EMPTY_PROPERTY);
      setSelectedTab(TABS.rent);
    } else {
      setFetchRinsUpsellData(true);
    }
  }, [encodedZuid]);

  useEffect(() => {
    if (fetchRinsUpsellData && !isLoadingRinsUpsellData) {
      let newProperty;

      if (!isErrorRinsUpsellData && rinsUpsellData) {
        newProperty = { ...EMPTY_PROPERTY, rinsSectionDto: { rinsCard: rinsUpsellData.renterInsuranceCard } };
      } else {
        newProperty = EMPTY_PROPERTY;
      }

      setAddedProperties((prev) => [...prev, newProperty]);
      setSelectedProperty(newProperty);
      setSelectedTab(TABS.rent);
      setFetchRinsUpsellData(false);
    }
  }, [fetchRinsUpsellData, isLoadingRinsUpsellData, isErrorRinsUpsellData, rinsUpsellData]);

  const handleBreadcrumbClick = useCallback((): void => {
    if (defaultProperty) {
      setSelectedProperty(defaultProperty);
      setSelectedTab(TABS.rent);
    }
  }, [defaultProperty]);

  return {
    selectedProperty,
    allProperties,
    defaultProperty,
    showBreadcrumb,

    selectedTab,
    setSelectedTab,

    handlePropertyChange,
    handleAddRental,
    handleBreadcrumbClick,
    isLoadingRinsUpsellData,
  };
};
